import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import TopicCard from '@/components/feature/TopicCard';
import { topicList } from '@/mocks/topics';
import { categories } from '@/mocks/site';

const filterKeys = ['全部', ...categories.map((c) => c.key)];
const sortOptions = [
  { key: 'heat', label: '按热度' },
  { key: 'update', label: '按更新条数' },
  { key: 'latest', label: '按最新' },
];

function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') || '全部';
  const [activeCategory, setActiveCategory] = useState(
    filterKeys.includes(initialCategory) ? initialCategory : '全部',
  );
  const [sortBy, setSortBy] = useState('heat');

  const handleCategory = (key: string) => {
    setActiveCategory(key);
    if (key === '全部') {
      setSearchParams({});
    } else {
      setSearchParams({ category: key });
    }
  };

  const filtered = useMemo(() => {
    const list =
      activeCategory === '全部'
        ? [...topicList]
        : topicList.filter((t) => t.category === activeCategory);

    if (sortBy === 'heat') {
      list.sort((a, b) => b.heat - a.heat);
    } else if (sortBy === 'update') {
      list.sort((a, b) => b.updateCount - a.updateCount);
    } else {
      list.sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1));
    }
    return list;
  }, [activeCategory, sortBy]);

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '网报吃瓜专题库',
      description: '网报吃瓜专题库按分类汇总正在跟踪的热点专题，每个专题包含完整事件时间线与媒体报道对比。',
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: topicList.map((topic, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: topic.title,
        url: `${window.location.origin}/topic/${topic.slug}`,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
        { '@type': 'ListItem', position: 2, name: '专题库', item: `${window.location.origin}/topics` },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="专题库 - 网报吃瓜热点专题，事件脉络与报道对比一站看全"
        description="网报吃瓜专题库汇总影视、综艺、音乐、网络、品牌、科技六大方向的热点专题，每个专题梳理事件起因、扩散路径与各方报道差异。"
        keywords="网报吃瓜专题,网报吃瓜媒体,网报吃瓜报道,热点专题,网报吃瓜转载"
        canonicalPath="/topics"
        schema={schema}
      />

      <div className="w-full px-4 pt-24 md:px-8 md:pt-32">
        <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '专题库' }]} />

        <header className="mt-6 border-b border-background-200/80 pb-8">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
            <i className="ri-route-line" />
            事件脉络
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
            网报吃瓜热点专题库
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            每个专题围绕一个热点事件展开，把起因、扩散、官方回应、媒体报道差异与后续进展按时间线整理在一起。选一个方向，挑一个事件，就能把整件事看明白。
          </p>
        </header>
      </div>

      <div className="w-full px-4 py-8 md:px-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {filterKeys.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => handleCategory(key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  activeCategory === key
                    ? 'bg-primary-500 text-foreground-950'
                    : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-700'
                }`}
              >
                {key}
              </button>
            ))}
          </div>

          <div className="inline-flex rounded-full border border-background-300 bg-background-50 p-1">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setSortBy(option.key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  sortBy === option.key
                    ? 'bg-secondary-500 text-background-50'
                    : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-5 text-sm text-foreground-600">
          共 <strong className="text-foreground-950">{filtered.length}</strong> 个专题
          {activeCategory !== '全部' && ` · ${activeCategory}`}
        </p>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((topic) => (
            <TopicCard key={topic.slug} topic={topic} />
          ))}
        </div>
      </div>
    </SiteLayout>
  );
}

export default Topics;